import { Editor, type IAllProps } from '@tinymce/tinymce-react';
import { colorTextPlaceholder } from '@arkestro/arkestro-design-system';
import { Skeleton } from 'antd-v3';
import useTranslation from 'next-translate/useTranslation';
import { FC, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import DOMPurify from 'dompurify';
import {
  NEXT_PUBLIC_TINYMCE_API_KEY
} from '@utils/constants';
import { sanitizeTextContent } from '@utils/sanitizeTextContent';
import InputField from '@components/shared/forms/InputField/InputField';

export const RICH_TEXT_CONTENT_CLASS = 'rich-text-editor__content';

interface Props {
  disabled?: boolean;
  labelText?: string;
  maxHeight?: number;
  minHeight?: number;
  onChange?: (_val: string) => void;
  placeholder?: string;
  // Disabled mode: show plain HTML instead of loading TinyMCE.
  renderDisabledAsPlain?: boolean;
  required?: boolean;
  // Note: statusbar must be enabled for resize to work — TinyMCE renders the handle in the statusbar.
  resize?: NonNullable<IAllProps['init']>['resize'];
  showOptional?: boolean;
  statusbar?: boolean;
  value?: string;
}

const RichTextEditor: FC<Props> = ({
  disabled,
  value,
  onChange,
  labelText = '',
  minHeight = 300,
  maxHeight = 600,
  required = false,
  renderDisabledAsPlain = false,
  resize = 'both',
  showOptional = false,
  statusbar = false,
  placeholder,
}: Props) => {
  const { t } = useTranslation('common');
  const [initialValue] = useState(value);
  const flags = useFlags();
  const { useTinymceEditor = true } = flags;
  const [loading, setLoading] = useState(useTinymceEditor);
  const rawValue = value || '';
  // sanitizeTextContent strips every tag, so a description made up of only non-text
  // content (an image, a divider, a table with empty cells) would otherwise be treated
  // as empty and short-circuit to null. Detect tags that carry visible content of their own.
  const hasContent = sanitizeTextContent(rawValue).length > 0 || /<(img|hr|table)\b/i.test(rawValue);
  const renderPlain = disabled && renderDisabledAsPlain && hasContent;
  const sanitizedHtml = useMemo(
    () => (typeof window === 'undefined' ? rawValue : DOMPurify.sanitize(rawValue)),
    [rawValue]
  );

  // If caller wants plain disabled view but there is no real content, render nothing to avoid blank space.
  if (disabled && renderDisabledAsPlain && !hasContent) return null;

  if (renderPlain) {
    return (
      <>
        {labelText && (
          <label className='rich-text-editor__label' htmlFor='name'>
            {labelText}
            {required && !disabled && <span> *</span>}
            {!required && showOptional && !disabled && <span> ({t('general.optional')})</span>}
          </label>
        )}
        <div aria-label={labelText} className='rich-text-editor rich-text-editor--readonly'>
          <div
            className={RICH_TEXT_CONTENT_CLASS}
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {labelText && (
        <label className='rich-text-editor__label' htmlFor='name'>
          {labelText}
          {required && !disabled && <span> *</span>}
          {!required && showOptional && !disabled && <span> ({t('general.optional')})</span>}
        </label>
      )}
      {loading && <Skeleton active className='rich-text-editor-skeleton' paragraph={{ rows: 5 }} />}
      <div aria-label={labelText} className={`rich-text-editor ${loading}`}>
        { useTinymceEditor && (
          <Editor
            apiKey={NEXT_PUBLIC_TINYMCE_API_KEY}
            disabled={disabled}
            init={{
              branding: false,
              color_map: [
                '#2DC26B', 'Green',
                '#F1C40F', 'Yellow',
                '#E03E2D', 'Red',
                '#B96AD9', 'Purple',
                '#169179', 'Dark Turquoise',
                '#E67E23', 'Orange',
                '#BA372A', 'Dark Red',
                '#843FA1', 'Dark Purple',
                '#236FA1', 'Dark Blue',
                '#CED4D9', 'Medium Gray',
                '#95A5A6', 'Gray',
                '#7E8C8D', 'Dark Gray',
                '#34495E', 'Navy Blue'
              ],
              content_style:
                'body { font-family:Helvetica,Arial,sans-serif; font-size:14px, line-height: 1; } '
                  + 'h1,h2,h3,h4,h5,h6, p{margin-block-start: .5rem; margin-block-end: .5rem;} '
                  + `.mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { color: ${colorTextPlaceholder}; }`,
              elementpath: false,
              height: minHeight,
              max_height: maxHeight,
              menubar: 'edit insert view format table tools help',
              min_height: minHeight,
              placeholder,
              plugins: [
                'autolink',
                'autoresize',
                'lists',
                'link',
                'charmap',
                'preview',
                'anchor',
                'searchreplace',
                'visualblocks',
                'fullscreen',
                'insertdatetime',
                'table'
              ],
              removed_menuitems: 'fontfamily',
              resize,
              statusbar,
              toolbar:
                'undo redo | blocks | bold italic forecolor backcolor | '
                  + 'alignleft aligncenter alignright alignjustify | '
                  + 'bullist numlist outdent indent | removeformat | code table',
              toolbar_mode: 'sliding',
            }}
            initialValue={initialValue}
            onEditorChange={onChange}
            onInit={() => {
              setLoading(false);
            }}
            value={value}
          />)
        }
        {!useTinymceEditor && (
          <InputField
            disabled={disabled}
            hasBorders
            name='Description'
            onChange={e => onChange?.(e.target.value)}
            readOnly={disabled}
            textArea
            type='text'
            value={value}
          />
        )}
      </div>
    </>
  );
};

export default RichTextEditor;
